import classNames from 'classnames'
import { FiArrowDownRight, FiArrowUpLeft, FiRepeat } from 'react-icons/fi'
import { IoWalletOutline } from 'react-icons/io5'
import { TMyTransactionKind } from './helpers'

// The marks a row and its details sheet share, drawn the way the fund tab's
// ledger draws them so a payment looks like the same thing in both places.

const TILES: Record<
    TMyTransactionKind,
    { Icon: typeof FiArrowDownRight; className: string; label: string }
> = {
    in: {
        Icon: FiArrowDownRight,
        className: 'bg-success-2 text-success-on',
        label: 'دریافتی',
    },
    out: {
        Icon: FiArrowUpLeft,
        className: 'bg-danger-soft text-danger-on',
        label: 'پرداختی',
    },
    wallet: {
        Icon: IoWalletOutline,
        className: 'bg-brand-soft text-brand-deep',
        label: 'شارژ کیف پول',
    },
    move: {
        Icon: FiRepeat,
        className: 'bg-divider text-text-secondary',
        label: 'جابه‌جایی',
    },
}

/** Which way the money went — by shape as well as colour. */
export function KindTile({
    kind,
    large = false,
}: {
    kind: TMyTransactionKind
    large?: boolean
}) {
    const { Icon, className } = TILES[kind]
    return (
        <span
            aria-hidden
            className={classNames(
                'flex shrink-0 items-center justify-center',
                large ? 'h-12 w-12 rounded-[14px]' : 'h-9 w-9 rounded-[10px]',
                className
            )}
        >
            <Icon className={large ? 'h-6 w-6' : 'h-[18px] w-[18px]'} />
        </span>
    )
}

export const kindLabel = (kind: TMyTransactionKind) => TILES[kind].label

const SIGNS: Record<TMyTransactionKind, string> = {
    in: '+',
    wallet: '+',
    out: '−',
    move: '',
}

// Only money that reached the member is green. What they paid is black, not
// red: an installment paid on time is the list's most ordinary row, and a
// page of red reads like a page of trouble. A move carries no sign at all —
// it never touched their pocket.
const AMOUNT_COLOURS: Record<TMyTransactionKind, string> = {
    in: 'text-success-on',
    wallet: 'text-brand-deep',
    out: 'text-black',
    move: 'text-text-body',
}

export function KindAmount({
    amount,
    kind,
    large = false,
}: {
    amount: number | null | undefined
    kind: TMyTransactionKind
    large?: boolean
}) {
    return (
        <span
            className={classNames(
                'whitespace-nowrap font-bold',
                large ? 'text-2xl leading-9' : 'text-[15px] leading-6',
                AMOUNT_COLOURS[kind]
            )}
        >
            {/* The sign is isolated with the digits: `+` and `−` are
                bidi-neutral, and loose in an RTL run they are painted at the
                far end of «… تومان», away from the number. */}
            <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>
                {SIGNS[kind]}
                {(amount ?? 0).toLocaleString()}
            </span>{' '}
            <span
                className={classNames(
                    'font-normal',
                    large ? 'text-sm' : 'text-[11px]'
                )}
            >
                تومان
            </span>
        </span>
    )
}

/** «۱۲٬۳۰۰٬۰۰۰ تومان» for a table cell. */
export function Toman({ amount }: { amount: number }) {
    return (
        <>
            <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>
                {amount.toLocaleString()}
            </span>{' '}
            <span className="text-[11px] font-normal">تومان</span>
        </>
    )
}
